import ParameterComponentTag from './ParameterComponentTag';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Drawer, Empty, Grid, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/tr';
import datePickerLocale from 'antd/lib/date-picker/locale/tr_TR';
import WeeklyPlanDrawer from './WeeklyPlanDrawer';

import { useNavigate } from 'react-router-dom';
import { APP_LOCAL_DATE_FORMAT } from 'config/constants';
import { openNotification } from '../../../utils';
import { getMissingReportFormatDrawerList, getReportFormatWeeklyStatus, getReportFormatStatusSummary, listReportFormatsAll } from '../services/report-formatService';
import { ReportFormat, ReportFormatMonthlyInspection, ReportFormatMonthlyStatus, ReportFormatStatusSummary } from '../types/ReportFormat';

dayjs.extend(isoWeek);

const formatDate = (value?: string | null) => value ? dayjs(value).format(APP_LOCAL_DATE_FORMAT) : '-';

const renderStatusTag = (value: boolean | null | undefined, falseLabel = 'Hayır') => {
  if (value === true) {
    return <Tag color='green'>Evet</Tag>;
  }
  return <Tag color='red'>{value === null || value === undefined ? 'Yok' : falseLabel}</Tag>;
};

const shouldShowEmptyStatus = (value: boolean | null | undefined) => value !== true;

type ReportFormatListType = 'all' | 'active' | 'tested' | 'missing';

const reportFormatListTitles: Record<ReportFormatListType, string> = {
  all: 'Rapor Formatı',
  missing: 'Rapor Formatı Olmayanlar',
  active: 'Aktif Rapor Formatı',
  tested: 'Test Edilen Rapor Formatı',
};

const matchesReportFormatList = (reportFormat: ReportFormat, type: ReportFormatListType) => {
  if (type === 'active') {
    return reportFormat.isActive === true;
  }
  if (type === 'tested') {
    return reportFormat.isActive === true && reportFormat.isTested === true;
  }
  return true;
};

const sortReportFormatsByTitle = (reportFormats: ReportFormat[]) => [...reportFormats].sort(
  (first, second) => String(first.title || '').localeCompare(String(second.title || ''), 'tr'),
);

export default function ReportFormatMonthlyStatusPage() {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const summaryCardColumnStyle = {
    display: 'flex',
    ...(screens.xl ? { flex: '0 0 14.285714%', maxWidth: '14.285714%' } : {}),
  };
  const summaryCardStyle = { flex: 1, width: '100%' };
  const [selectedWeek, setSelectedWeek] = useState<Dayjs>(() => dayjs().locale('tr').startOf('isoWeek'));
  const [data, setData] = useState<ReportFormatMonthlyStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ReportFormatStatusSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ReportFormatMonthlyStatus | null>(null);
  const [reportFormatListType, setReportFormatListType] = useState<ReportFormatListType | null>(null);
  const [reportFormats, setReportFormats] = useState<ReportFormat[]>([]);
  const [reportFormatsLoading, setReportFormatsLoading] = useState(false);

  const [weeklyPlanOpen, setWeeklyPlanOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData([]);
    setSelectedRecord(null);
    getReportFormatWeeklyStatus(selectedWeek.format('YYYY-MM-DD'))
      .then((response) => { if (!cancelled) setData(response || []); })
      .catch(() => { if (!cancelled) openNotification('error', 'Hata', 'Rapor format durumu yüklenemedi.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedWeek]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      setSummary(await getReportFormatStatusSummary());
    } catch (error) {
      openNotification('error', 'Hata', 'Rapor formatı özeti yüklenemedi.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const openPlanDrawer = useCallback((record: ReportFormatMonthlyStatus) => {
    setSelectedRecord(record);
  }, []);

  const closePlanDrawer = useCallback(() => {
    setSelectedRecord(null);
  }, []);

  const openReportFormatDrawer = useCallback(async (type: ReportFormatListType) => {
    setReportFormatListType(type);
    setReportFormats([]);
    setReportFormatsLoading(true);
    try {
      if (type === 'missing') {
        setReportFormats(sortReportFormatsByTitle(await getMissingReportFormatDrawerList()));
        return;
      }
      const response = await listReportFormatsAll('title,asc');
      setReportFormats(sortReportFormatsByTitle((response || []).filter((reportFormat) => matchesReportFormatList(reportFormat, type))));
    } catch (error) {
      openNotification('error', 'Hata', 'Rapor formatları yüklenemedi.');
    } finally {
      setReportFormatsLoading(false);
    }
  }, []);

  const closeReportFormatDrawer = useCallback(() => {
    setReportFormatListType(null);
    setReportFormats([]);
  }, []);

  const canOpenControl = useCallback((record: ReportFormatMonthlyInspection) => Boolean(record.controlUrl), []);

  const openControl = useCallback((record: ReportFormatMonthlyInspection) => {
    if (!canOpenControl(record) || !record.controlUrl) {
      return;
    }
    navigate(record.controlUrl);
  }, [canOpenControl, navigate]);

  const inspectionColumns: ColumnsType<ReportFormatMonthlyInspection> = useMemo(() => [
    {
      title: 'Firma',
      dataIndex: 'companyName',
      render: (value?: string | null) => <b>{value || '-'}</b>,
    },
    {
      title: 'Adres',
      dataIndex: 'address',
      render: (value?: string | null) => value || '-',
    },
    {
      title: 'Kontrol Tarihi',
      dataIndex: 'date',
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Uzman',
      render: (_: unknown, record: ReportFormatMonthlyInspection) =>
        [record.firstName, record.lastName].filter(Boolean).join(' ') || '-',
    },
    {
      title: 'Sıra',
      dataIndex: 'orderNo',
      width: 80,
      render: (value?: number | null) => value ?? '-',
    },
    {
      title: 'İşlem',
      width: 140,
      render: (_: unknown, record: ReportFormatMonthlyInspection) => {
        const disabled = !canOpenControl(record);
        return (
          <Tooltip title={disabled ? 'Kontrol bağlantısı eksik' : undefined}>
            <span>
              <Button
                size='small'
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  openControl(record);
                }}
              >
                Kontrole Git
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ], [canOpenControl, openControl]);

  const columns: ColumnsType<ReportFormatMonthlyStatus> = useMemo(() => [
    {
      title: 'Parametre',
      dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || '', 'tr'),
      render: (value: string | null | undefined, record: ReportFormatMonthlyStatus) => <>
        {value || '-'}<ParameterComponentTag toolId={record.toolId} toolName={record.toolName} />
      </>,
    },
    {
      title: 'Plan Sayısı',
      dataIndex: 'taskCount',
      sorter: (a, b) => Number(a.taskCount || 0) - Number(b.taskCount || 0),
    },
    {
      title: 'Görev Sayısı',
      key: 'inspectionCount',
      sorter: (a, b) => (a.inspections || []).length - (b.inspections || []).length,
      render: (_: unknown, record: ReportFormatMonthlyStatus) => (record.inspections || []).length,
    },
    {
      title: 'Rapor Formatı',
      dataIndex: 'hasReportFormat',
      render: (value: boolean | null | undefined) => renderStatusTag(value),
    },
    {
      title: 'Aktif',
      dataIndex: 'isActive',
      render: (value: boolean | null | undefined, record: ReportFormatMonthlyStatus) =>
        shouldShowEmptyStatus(record.hasReportFormat) ? '-' : renderStatusTag(value),
    },
    {
      title: 'Test Edildi',
      dataIndex: 'isTested',
      render: (value: boolean | null | undefined, record: ReportFormatMonthlyStatus) =>
        shouldShowEmptyStatus(record.hasReportFormat) || shouldShowEmptyStatus(record.isActive)
          ? '-'
          : renderStatusTag(value),
    },
    {
      title: 'İlk Denetim Tarihi',
      dataIndex: 'firstDate',
      sorter: (a, b) => String(a.firstDate || '').localeCompare(String(b.firstDate || '')),
      render: (value?: string | null) => formatDate(value),
    },
    {
      title: 'Plan',
      render: (_: unknown, record: ReportFormatMonthlyStatus) => (
        <Button size='small' onClick={(event) => { event.stopPropagation(); openPlanDrawer(record); }}>
          Plan
        </Button>
      ),
    },
  ], [openPlanDrawer]);

  const reportFormatColumns: ColumnsType<ReportFormat> = useMemo(() => {
    if (reportFormatListType === 'missing') {
      return [
        {
          title: 'Parametre',
          dataIndex: 'title',
          render: (value?: string | null) => value || '-',
        },
        {
          title: 'Aktif',
          dataIndex: 'isActive',
          width: 120,
          render: (value: boolean) => renderStatusTag(value),
        },
      ];
    }

    return [
    {
      title: 'Rapor Formatı',
      dataIndex: 'title',
      render: (value?: string | null) => value || '-',
    },
    {
      title: 'Parametre',
      dataIndex: ['attribute', 'name'],
      render: (value?: string | null) => value || '-',
    },
    {
      title: 'Sürüm',
      dataIndex: 'version',
      width: 130,
      render: (value?: string | null) => value || '-',
    },
    ];
  }, [reportFormatListType]);

  const summaryCards: Array<{ type: ReportFormatListType; value: number }> = [
    { type: 'all', value: summary?.reportFormatCount ?? 0 },
    { type: 'missing', value: summary?.missingReportFormatParameterCount ?? 0 },
    { type: 'active', value: summary?.activeReportFormatCount ?? 0 },
    { type: 'tested', value: summary?.testedActiveReportFormatCount ?? 0 },
  ];

  return (
    <>
      <Row align='stretch' gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} style={summaryCardColumnStyle}>
          <Card loading={summaryLoading} style={summaryCardStyle}>
            <Statistic title='Akreditasyonu Zorunlu Olanlar' value={summary?.accreditationRequiredParameterCount ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} style={summaryCardColumnStyle}>
          <Card loading={summaryLoading} style={summaryCardStyle}>
            <Statistic title='Akredite Olanlar' value={summary?.accreditedParameterCount ?? 0} />
          </Card>
        </Col>
        <Col xs={24} sm={12} style={summaryCardColumnStyle}>
          <Card loading={summaryLoading} style={summaryCardStyle}>
            <Statistic title='Aktif Parametre' value={summary?.totalParameterCount ?? 0} />
          </Card>
        </Col>
        {summaryCards.map(({ type, value }) => (
          <Col xs={24} sm={12} style={summaryCardColumnStyle} key={type}>
            <Card
              loading={summaryLoading}
              style={summaryCardStyle}
              hoverable
              role='button'
              tabIndex={0}
              aria-label={`${reportFormatListTitles[type]} listesini aç`}
              onClick={() => openReportFormatDrawer(type)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openReportFormatDrawer(type);
                }
              }}
            >
              <Statistic title={reportFormatListTitles[type]} value={value} />
            </Card>
          </Col>
        ))}
      </Row>
      <Card className='br-30px'>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Space wrap align='center' style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>Rapor Format Durumu</Typography.Title>
            <Space wrap>
              <Button onClick={() => setWeeklyPlanOpen(true)}>Haftalık Plan</Button>
              <DatePicker
                aria-label='Hafta seçimi'
                locale={datePickerLocale}
                picker='week'
                allowClear={false}
                value={selectedWeek}
                format={(value) => `${value.startOf('isoWeek').format('DD.MM.YYYY')} – ${value.endOf('isoWeek').format('DD.MM.YYYY')}`}
                onChange={(value) => setSelectedWeek((value || dayjs()).locale('tr').startOf('isoWeek'))}
              />
            </Space>
          </Space>
          <Table
            rowKey='attributeId'
            dataSource={data}
            columns={columns}
            loading={loading}
            pagination={false}
            onRow={(record) => ({
              onClick: () => openPlanDrawer(record),
              style: { cursor: 'pointer' },
            })}
          />
        </Space>
      </Card>
      <WeeklyPlanDrawer open={weeklyPlanOpen} onClose={() => setWeeklyPlanOpen(false)} />
      <Drawer
        open={!!selectedRecord}
        title={selectedRecord?.name ? `${selectedRecord.name} Planları` : 'Planlar'}
        onClose={closePlanDrawer}
        destroyOnClose
        width={900}
      >
        {(selectedRecord?.inspections || []).length ? (
          <Table
            rowKey={(record, index) => `${record.taskId || 'task'}-${record.orderNo || 'order'}-${index}`}
            dataSource={selectedRecord?.inspections || []}
            columns={inspectionColumns}
            pagination={false}
            onRow={(record) => ({
              onClick: () => openControl(record),
              style: { cursor: canOpenControl(record) ? 'pointer' : 'default' },
            })}
          />
        ) : (
          <Empty description='Bu parametre için plan bulunamadı.' />
        )}
      </Drawer>
      <Drawer
        open={!!reportFormatListType}
        title={reportFormatListType ? `${reportFormatListTitles[reportFormatListType]} Listesi` : 'Rapor Formatı Listesi'}
        onClose={closeReportFormatDrawer}
        destroyOnClose
        width={720}
      >
        <Table
          rowKey={(record) => record.id || `${record.title || 'report-format'}-${record.version || ''}`}
          dataSource={reportFormats}
          columns={reportFormatColumns}
          loading={reportFormatsLoading}
          pagination={false}
          locale={{ emptyText: <Empty description={reportFormatListType === 'missing' ? 'Rapor formatı olmayan parametre bulunamadı.' : 'Gösterilecek rapor formatı bulunamadı.'} /> }}
        />
      </Drawer>
    </>
  );
}
